import { envVariables } from '@fabric-ui/utils';
import { normalizeAuthenticationResponse } from '../auth.types';
import { AuthEventType, AuthHub } from '../AuthHub';
import {
  retrieveConnectionDetailsFromAuthConfig,
  ZidAuthLocalStorageKeys,
  ZidAuthSessionStorageKeys,
  ZidAuthUrlParams,
} from './tokenUtils';

const attemptFetch = async (url: string, options: RequestInit, maxRetries: number, attempt: number): Promise<Response> => {
  try {
    return await fetch(url, options);
  } catch (error) {
    console.error(`Attempt ${attempt} of ${maxRetries} to fetch token failed:`, error);

    if (attempt >= maxRetries) {
      throw new Error('Request failed after multiple retries.');
    }

    const delay = 1000 * 2 ** (attempt - 1);
    await new Promise(resolve => {
      setTimeout(resolve, delay);
    });

    return attemptFetch(url, options, maxRetries, attempt + 1);
  }
};

const fetchWithRetry = async (url: string, options: RequestInit, maxRetries = 3) => attemptFetch(url, options, maxRetries, 1);

const exchangeCodeForToken = async (code: string, state: string, zidTenantServiceDomain: string) => {
  const tokenRequestBody = {
    authorizationCode: code,
    redirectUri: `${window.location.origin}`,
    state,
    zidDomain: `https://${zidTenantServiceDomain}`,
  };

  const response = await fetchWithRetry(`${envVariables.VITE_WEBSERVER_API_URL}/auth-config/token`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: new URLSearchParams(tokenRequestBody).toString(),
  });

  const res = await response.json();

  if (!response.ok) {
    const errorMessage = res.error_description || res.error || 'An unknown error occurred during token exchange.';
    throw new Error(`Token exchange failed: ${errorMessage}`);
  }

  return res;
};

const setAuthDetailsInLS = res => {
  // Normalize response to handle both old (zidAuthenticationResponse) and new (authenticationResponse) formats
  const { tokens, session } = normalizeAuthenticationResponse(res);

  localStorage.setItem(ZidAuthLocalStorageKeys.idToken, tokens.id_token);
  localStorage.setItem(ZidAuthLocalStorageKeys.accessToken, tokens.access_token);
  localStorage.setItem(ZidAuthLocalStorageKeys.session, JSON.stringify(session));
  localStorage.setItem(ZidAuthLocalStorageKeys.refreshToken, tokens.refresh_token);
};

export const handleCodeFlow = async () => {
  const params = new URLSearchParams(window.location.search);
  const code = params.get(ZidAuthUrlParams.code);
  const state = params.get(ZidAuthUrlParams.state);
  const error = params.get(ZidAuthUrlParams.error);

  window.history.replaceState(window.history.state, '', window.location.origin);

  if (error) {
    const errorDescription = params.get(ZidAuthUrlParams.errorDescription);
    throw new Error(`OAuth Error: ${error} - ${errorDescription || 'No description provided.'}`);
  }

  const { zidTenantServiceDomain } = retrieveConnectionDetailsFromAuthConfig();

  const storedState = sessionStorage.getItem(ZidAuthSessionStorageKeys.oauthState);

  if (code && state) {
    try {
      if (!storedState || state !== storedState) {
        throw new Error('Invalid state parameter. Possible CSRF attack.');
      }

      const res = await exchangeCodeForToken(code, state, zidTenantServiceDomain);

      setAuthDetailsInLS(res);

      const originalPath = sessionStorage.getItem(ZidAuthSessionStorageKeys.postLoginRedirectPath) || '/';

      window.history.replaceState(window.history.state, '', originalPath);

      sessionStorage.removeItem(ZidAuthSessionStorageKeys.oauthState);
      AuthHub.dispatch({ event: AuthEventType.SignInWithRedirect, data: res });

      const customState = sessionStorage.getItem(ZidAuthSessionStorageKeys.customState);
      if (customState) {
        AuthHub.dispatch({ event: AuthEventType.CustomOAuthState, data: customState });
      }
    } catch (e) {
      console.error('Failed to complete the authentication flow.', e);
      throw e;
    } finally {
      sessionStorage.removeItem(ZidAuthSessionStorageKeys.oauthState);
      sessionStorage.removeItem(ZidAuthSessionStorageKeys.postLoginRedirectPath);
    }
  } else if (!code && storedState) {
    sessionStorage.clear();
  }
};
